import { Reference } from '@approvalmax/types';
import { CheckboxEditor, DropdownEditor, FocusImplementer, NumberEditor, TextEditor } from '@approvalmax/ui';
import { useAtom, useSetAtom } from 'jotai';
import { constants } from 'modules/common';
import { domain } from 'modules/data';
import { PlainDataProvider } from 'modules/data-providers';
import { useSelector } from 'modules/react-redux';
import { useGetRecognizedOcrAttachment } from 'pages/requestForm/hooks/useGetRecognizedOcrAttachment';
import { useOcrData } from 'pages/requestForm/hooks/useOcrData';
import { requestSelector } from 'pages/requestForm/selectors/pageSelectors';
import { ColumnDefinition } from 'pages/requestForm/selectors/types/ColumnDefinition';
import { useCallback, useEffect, useMemo } from 'react';
import { useNetSuiteVendorFieldSettings } from 'shared/hooks';

import { setCurrencyPrecision } from '../../helpers';
import { useChangeNetSuiteRequest, useCurrencyPrecision } from '../../hooks';
import {
    isFieldMandatory,
    isFieldReadOnly,
    isFieldVisible,
} from '../../NetSuiteRequestForm/NetSuiteRequestForm.helpers';
import { DropdownEditorContainer } from '../../NetSuiteRequestForm/NetSuiteRequestForm.styles';
import { netSuiteVendorFieldValuesState } from '../../NetSuiteVendorRequestForm/NetSuiteVendorRequestForm.states';
import {
    NetSuiteSubsidiaryListItem,
    NetSuiteVendorAddressListItem,
    NetSuiteVendorFields,
} from '../../NetSuiteVendorRequestForm/NetSuiteVendorRequestForm.types';
import NetSuiteCountriesDataProvider from '../NetSuiteCountriesDataProvider/NetSuiteCountriesDataProvider';
import { NetSuiteSubsidiaryTaxCodesDataProvider } from '../NetSuiteSubsidiaryTaxCodesDataProvider/NetSuiteSubsidiaryTaxCodesDataProvider';
import { defaultAddressLine } from './NetSuiteVendorForm.constants';
import {
    extractOcrAddress,
    extractOcrCurrency,
    generateUniqueAddressListItemId,
    generateUniqueSubsidiaryListItemId,
    isVendorAddressEmpty,
    isVendorSubsidiaryEmpty,
} from './NetSuiteVendorForm.helpers';
import { messages } from './NetSuiteVendorForm.messages';
import { CheckboxCell } from './NetSuiteVendorForm.styles';
import { UseFieldsParams } from './NetSuiteVendorForm.types';

const { netSuiteConstants } = constants;

export const useFields = (params: UseFieldsParams) => {
    const { defaultCurrency, defaultSubsidiary, currencies, details, shouldDispatchChangeEvent } = params;

    const changeNetSuiteRequest = useChangeNetSuiteRequest();

    const [fieldValues, setFieldValues] = useAtom(netSuiteVendorFieldValuesState);

    const request = useSelector(requestSelector);

    const ocrAttachment = useGetRecognizedOcrAttachment(request);

    const { data: ocrData } = useOcrData(request, ocrAttachment?.id);

    const currencyPrecision = useCurrencyPrecision(fieldValues?.currency);

    useEffect(() => {
        setFieldValues((current) => {
            if (!current) {
                return null;
            }

            return {
                ...current,
                primarySubsidiary: defaultSubsidiary || null,
            };
        });
    }, [defaultSubsidiary, setFieldValues]);

    useEffect(() => {
        setFieldValues((current) => {
            if (!current) {
                return null;
            }

            return {
                ...current,
                currency: defaultCurrency,
                currencies: defaultCurrency ? [defaultCurrency] : [],
            };
        });
    }, [defaultCurrency, setFieldValues]);

    useEffect(() => {
        const currency = currencies.find(
            (currencyItem) => currencyItem.id === fieldValues?.primarySubsidiary?.currencyInternalId
        );

        setFieldValues((current) => {
            if (!current) {
                return null;
            }

            return {
                ...current,
                currency: currency || null,
                currencies: currency ? [currency] : [],
            };
        });
    }, [currencies, fieldValues?.primarySubsidiary, setFieldValues]);

    useEffect(() => {
        if (fieldValues?.isPerson) {
            setFieldValues((current) => {
                if (!current) {
                    return null;
                }

                return {
                    ...current,
                    webAddress: '',
                };
            });
        } else {
            setFieldValues((current) => {
                if (!current) {
                    return null;
                }

                return {
                    ...current,
                    salutation: '',
                    firstName: '',
                    middleName: '',
                    lastName: '',
                    jobTitle: '',
                };
            });
        }
    }, [fieldValues?.isPerson, setFieldValues]);

    useEffect(() => {
        const ocrCurrency = extractOcrCurrency(ocrData?.document, currencies);
        const ocrAddress = extractOcrAddress(ocrData?.document);

        let addressLines: NetSuiteVendorAddressListItem[] = [];

        if (ocrAddress) {
            addressLines.push(ocrAddress);
        }

        if (details?.addressLines.length) {
            addressLines = addressLines.concat(
                details.addressLines.map((address) => ({
                    ...address,
                    id: generateUniqueAddressListItemId(),
                }))
            );
        } else if (!ocrAddress) {
            addressLines.push({
                ...defaultAddressLine,
                id: generateUniqueAddressListItemId(),
            });
        }

        const primaryCurrency = ocrCurrency || details?.primaryCurrency || defaultCurrency;
        const primaryCurrencyPrecision = currencies.find(({ id }) => id === primaryCurrency?.id)?.precision ?? 2;

        setFieldValues({
            isPerson: details?.type === domain.NetSuiteVendorType.Individual,
            companyName: ocrData?.document?.vendor?.name || details?.companyName || '',
            webAddress: details?.webAddress || '',
            salutation: details?.salutation || '',
            firstName: details?.firstName || '',
            middleName: details?.middleName || '',
            lastName: details?.lastName || '',
            jobTitle: details?.jobTitle || '',
            category: details?.category ? ({ ...details.category, name: details.category.text } as Reference) : null,
            comments: details?.comments || '',

            email: details?.email || '',
            phone: details?.phone || '',
            altPhone: details?.altPhone || '',
            fax: details?.fax || '',

            legalName: details?.legalName || '',
            accountNumber: details?.accountNumber || '',
            defaultExpenseAccount: details?.defaultExpenseAccount || null,
            defaultPayablesAccount: details?.defaultPayablesAccount || null,
            terms: details?.terms || null,
            creditLimit: details?.creditLimit
                ? setCurrencyPrecision(details.creditLimit, primaryCurrencyPrecision)
                : null,
            incoterm: details?.incoterm || null,
            taxNumber: ocrData?.document?.vendor?.taxNumber || details?.taxNumber || '',
            is1099Eligible: details?.is1099Eligible || false,
            isJobResource: details?.isJobResource || false,

            primarySubsidiary: defaultSubsidiary || null,
            subsidiaries: details?.subsidiaries.length
                ? details.subsidiaries.map((subsidiary) => {
                      const subsidiaryCurrencyPrecision =
                          currencies.find(({ id }) => id === subsidiary.subsidiary?.currencyInternalId)?.precision ?? 2;

                      return {
                          ...subsidiary,
                          creditLimit: setCurrencyPrecision(subsidiary.creditLimit, subsidiaryCurrencyPrecision),
                          id: generateUniqueSubsidiaryListItemId(),
                      };
                  })
                : [
                      {
                          id: generateUniqueSubsidiaryListItemId(),
                          subsidiary: defaultSubsidiary,
                          creditLimit: null,
                          taxCode: null,
                      },
                  ],

            currency: primaryCurrency,
            currencies: details?.currencies || (defaultCurrency ? [defaultCurrency] : []),

            addressLines,
        });
    }, [currencies, defaultCurrency, defaultSubsidiary, details, ocrData?.document, setFieldValues]);

    const onFieldChange = useCallback(
        <K extends keyof NetSuiteVendorFields>(key: K) =>
            (newValue: NetSuiteVendorFields[K]) => {
                setFieldValues((current) => {
                    if (!current) {
                        return null;
                    }

                    if (key === 'currency') {
                        return {
                            ...current,
                            creditLimit: setCurrencyPrecision(current.creditLimit, currencyPrecision),
                            [key]: newValue,
                        };
                    }

                    if (key === 'creditLimit') {
                        return {
                            ...current,
                            creditLimit: setCurrencyPrecision(newValue, currencyPrecision),
                        };
                    }

                    return {
                        ...current,
                        [key]: newValue,
                    };
                });

                shouldDispatchChangeEvent && changeNetSuiteRequest();
            },
        [currencyPrecision, shouldDispatchChangeEvent, changeNetSuiteRequest, setFieldValues]
    );

    return {
        fieldValues,
        onFieldChange,
    };
};

export const useSubsidiaryTable = (params: {
    defaultSubsidiary: domain.NetSuiteSubsidiaryItem | null;
    subsidiaryReferenceList: domain.NetSuiteSubsidiaryItem[];
    currencies: domain.NetSuiteCurrency[];
    primarySubsidiaryId: string | undefined;
    fieldSettings: ReturnType<typeof useNetSuiteVendorFieldSettings>;
    showErrors: boolean;
    shouldDispatchChangeEvent?: boolean;
}) => {
    const {
        defaultSubsidiary,
        subsidiaryReferenceList,
        currencies,
        primarySubsidiaryId,
        fieldSettings,
        showErrors,
        shouldDispatchChangeEvent,
    } = params;

    const setFieldValues = useSetAtom(netSuiteVendorFieldValuesState);

    const changeNetSuiteRequest = useChangeNetSuiteRequest();

    useEffect(() => {
        setFieldValues((fieldValues) => {
            if (!defaultSubsidiary || !fieldValues) {
                return fieldValues;
            }

            if (fieldValues.subsidiaries?.length === 0) {
                return {
                    ...fieldValues,
                    subsidiaries: [
                        {
                            id: generateUniqueSubsidiaryListItemId(),
                            subsidiary: defaultSubsidiary,
                            creditLimit: null,
                            taxCode: null,
                        },
                    ],
                };
            }

            if (fieldValues.subsidiaries?.length === 1) {
                return {
                    ...fieldValues,
                    subsidiaries: [
                        {
                            ...fieldValues.subsidiaries[0],
                            subsidiary: defaultSubsidiary,
                        },
                    ],
                };
            }

            const hasDefaultSubsidiary = fieldValues.subsidiaries?.find(
                ({ subsidiary }) => subsidiary?.id === defaultSubsidiary.id
            );

            if (!hasDefaultSubsidiary) {
                return {
                    ...fieldValues,
                    subsidiaries: [
                        ...(fieldValues.subsidiaries || []),
                        {
                            id: generateUniqueSubsidiaryListItemId(),
                            subsidiary: defaultSubsidiary,
                            creditLimit: null,
                            taxCode: null,
                        },
                    ],
                };
            }

            return fieldValues;
        });
    }, [defaultSubsidiary, setFieldValues]);

    const onSubsidiaryListItemAdd = useCallback(() => {
        setFieldValues((fieldValues) => {
            if (!fieldValues) {
                return null;
            }

            return {
                ...fieldValues,
                subsidiaries: [
                    ...(fieldValues.subsidiaries || []),
                    {
                        id: generateUniqueSubsidiaryListItemId(),
                        subsidiary: null,
                        creditLimit: null,
                        taxCode: null,
                    },
                ],
            };
        });

        shouldDispatchChangeEvent && changeNetSuiteRequest();
    }, [shouldDispatchChangeEvent, changeNetSuiteRequest, setFieldValues]);

    const onSubsidiaryListItemRemove = useCallback(
        (id: string) => {
            setFieldValues((fieldValues) => {
                if (!fieldValues) {
                    return null;
                }

                return {
                    ...fieldValues,
                    subsidiaries: (fieldValues.subsidiaries || []).filter((subsidiary) => subsidiary.id !== id),
                };
            });

            shouldDispatchChangeEvent && changeNetSuiteRequest();
        },
        [shouldDispatchChangeEvent, changeNetSuiteRequest, setFieldValues]
    );

    const onSubsidiaryItemUpdate = useCallback(
        <K extends keyof NetSuiteSubsidiaryListItem>(id: string, key: K, value: NetSuiteSubsidiaryListItem[K]) => {
            setFieldValues((fieldValues) => {
                if (!fieldValues) {
                    return null;
                }

                return {
                    ...fieldValues,
                    subsidiaries: (fieldValues.subsidiaries || []).map((subsidiary) => {
                        if (subsidiary.id === id) {
                            if (key === 'subsidiary') {
                                return {
                                    ...subsidiary,
                                    taxCode: null,
                                    [key]: value,
                                };
                            }

                            if (key === 'creditLimit') {
                                const currencyPrecision =
                                    currencies.find(({ id }) => id === subsidiary.subsidiary?.currencyInternalId)
                                        ?.precision ?? 2;

                                return {
                                    ...subsidiary,
                                    creditLimit: setCurrencyPrecision(value, currencyPrecision),
                                };
                            }

                            return {
                                ...subsidiary,
                                [key]: value,
                            };
                        }

                        return subsidiary;
                    }),
                };
            });

            shouldDispatchChangeEvent && changeNetSuiteRequest();
        },
        [currencies, shouldDispatchChangeEvent, changeNetSuiteRequest, setFieldValues]
    );

    const onSubsidiaryItemClone = useCallback(
        (id: string) => {
            setFieldValues((fieldValues) => {
                if (!fieldValues) {
                    return null;
                }

                const subsidiaryToClone = fieldValues.subsidiaries?.find((subsidiary) => subsidiary.id === id);

                if (!subsidiaryToClone) {
                    return fieldValues;
                }

                return {
                    ...fieldValues,
                    subsidiaries: [
                        ...(fieldValues.subsidiaries || []),
                        {
                            ...subsidiaryToClone,
                            id: generateUniqueSubsidiaryListItemId(),
                        },
                    ],
                };
            });

            shouldDispatchChangeEvent && changeNetSuiteRequest();
        },
        [shouldDispatchChangeEvent, changeNetSuiteRequest, setFieldValues]
    );

    const tableDef = useMemo<ColumnDefinition<NetSuiteSubsidiaryListItem>[]>(() => {
        return [
            {
                id: 'subsidiary',
                name: messages.subsidiary,
                readonly: () => false,
                invalid: (li) => {
                    if (!showErrors) {
                        return false;
                    }

                    const isEmptyLine = isVendorSubsidiaryEmpty(li);

                    return !isEmptyLine && !li.subsidiary;
                },
                cell: (li, { invalid }) => (
                    <FocusImplementer>
                        {({ registerFocusable }) => (
                            <DropdownEditorContainer>
                                <PlainDataProvider items={subsidiaryReferenceList || []}>
                                    <DropdownEditor
                                        ref={registerFocusable}
                                        invalid={invalid}
                                        value={li.subsidiary || null}
                                        singleLinePerItem={false}
                                        onChange={(value: NetSuiteSubsidiaryListItem['subsidiary']) => {
                                            onSubsidiaryItemUpdate(li.id, 'subsidiary', value);
                                        }}
                                        placeholder={messages.selectPlaceholder}
                                        theme='transparent'
                                        displayAttribute='name'
                                    />
                                </PlainDataProvider>
                            </DropdownEditorContainer>
                        )}
                    </FocusImplementer>
                ),
            },
            {
                id: 'creditLimit',
                name: messages.creditLimit,
                alignRight: true,
                cell: (li, { invalid }) => {
                    const currencyPrecision =
                        currencies.find(({ id }) => id === li.subsidiary?.currencyInternalId)?.precision ?? 2;

                    return (
                        <FocusImplementer>
                            {({ registerFocusable }) => (
                                <NumberEditor
                                    ref={registerFocusable}
                                    allowFloat
                                    invalid={invalid}
                                    value={li.creditLimit !== null ? li.creditLimit : null}
                                    onChange={(newValue: number | null) => {
                                        onSubsidiaryItemUpdate(li.id, 'creditLimit', newValue);
                                    }}
                                    placeholder={messages.inputPlaceholder}
                                    theme='transparent'
                                    align='right'
                                    displayFormat='currency'
                                    precision={currencyPrecision}
                                    min={0}
                                    max={netSuiteConstants.maxCreditLimit}
                                    changeOnBlur={false}
                                />
                            )}
                        </FocusImplementer>
                    );
                },
            },
            {
                id: 'taxCode',
                name: messages.taxCode,
                invalid: (li) => {
                    if (!showErrors) {
                        return false;
                    }

                    const isEmptyLine = isVendorSubsidiaryEmpty(li);

                    return (
                        !isEmptyLine &&
                        isFieldMandatory(fieldSettings.taxCode) &&
                        li.subsidiary?.id === primarySubsidiaryId &&
                        !li.taxCode
                    );
                },
                cell: (li, { invalid }) => (
                    <FocusImplementer>
                        {({ registerFocusable }) => (
                            <DropdownEditorContainer>
                                <NetSuiteSubsidiaryTaxCodesDataProvider subsidiaryId={li.subsidiary?.id}>
                                    <DropdownEditor
                                        ref={registerFocusable}
                                        invalid={invalid}
                                        value={li.taxCode || null}
                                        singleLinePerItem={false}
                                        onChange={(value: NetSuiteSubsidiaryListItem['taxCode']) => {
                                            onSubsidiaryItemUpdate(li.id, 'taxCode', value);
                                        }}
                                        placeholder={messages.selectPlaceholder}
                                        theme='transparent'
                                    />
                                </NetSuiteSubsidiaryTaxCodesDataProvider>
                            </DropdownEditorContainer>
                        )}
                    </FocusImplementer>
                ),
            },
        ];
    }, [showErrors, subsidiaryReferenceList, currencies, fieldSettings, primarySubsidiaryId, onSubsidiaryItemUpdate]);

    return {
        tableDef,
        onSubsidiaryListItemAdd,
        onSubsidiaryListItemRemove,
        onSubsidiaryItemClone,
    };
};

export const useAddressListTable = (
    fieldSettings: ReturnType<typeof useNetSuiteVendorFieldSettings>,
    showErrors: boolean,
    shouldDispatchChangeEvent?: boolean
) => {
    const setFieldValues = useSetAtom(netSuiteVendorFieldValuesState);

    const changeNetSuiteRequest = useChangeNetSuiteRequest();

    const onAddressListItemAdd = useCallback(() => {
        setFieldValues((fieldValues) => {
            if (!fieldValues) {
                return null;
            }

            const hasAddressLines = fieldValues.addressLines?.length !== 0;

            return {
                ...fieldValues,
                addressLines: [
                    ...(fieldValues.addressLines || []),
                    {
                        id: generateUniqueAddressListItemId(),
                        label: '',
                        defaultBilling: !hasAddressLines,
                        defaultShipping: !hasAddressLines,
                        primaryAddress: '',
                        secondaryAddress: '',
                        additionalAddress: '',
                        addressRepresentation: '',
                        addressee: '',
                        attention: '',
                        city: '',
                        country: null,
                        zip: '',
                        state: '',
                        phone: '',
                    },
                ],
            };
        });

        shouldDispatchChangeEvent && changeNetSuiteRequest();
    }, [shouldDispatchChangeEvent, changeNetSuiteRequest, setFieldValues]);

    const onAddressListItemRemove = useCallback(
        (id: string) => {
            setFieldValues((fieldValues) => {
                if (!fieldValues) {
                    return null;
                }

                return {
                    ...fieldValues,
                    addressLines: fieldValues.addressLines?.filter((address) => address.id !== id),
                };
            });

            shouldDispatchChangeEvent && changeNetSuiteRequest();
        },
        [shouldDispatchChangeEvent, changeNetSuiteRequest, setFieldValues]
    );

    const onAddressItemUpdate = useCallback(
        <K extends keyof NetSuiteVendorAddressListItem>(
            id: string,
            key: K,
            value: NetSuiteVendorAddressListItem[K]
        ) => {
            setFieldValues((fieldValues) => {
                if (!fieldValues) {
                    return null;
                }

                return {
                    ...fieldValues,
                    addressLines: fieldValues.addressLines?.map((address) => {
                        if (address.id === id) {
                            return {
                                ...address,
                                [key]: value,
                            };
                        }

                        if (address.id !== id && key === 'defaultBilling' && value) {
                            return {
                                ...address,
                                defaultBilling: false,
                            };
                        }

                        if (address.id !== id && key === 'defaultShipping' && value) {
                            return {
                                ...address,
                                defaultShipping: false,
                            };
                        }

                        return address;
                    }),
                };
            });

            shouldDispatchChangeEvent && changeNetSuiteRequest();
        },
        [shouldDispatchChangeEvent, changeNetSuiteRequest, setFieldValues]
    );

    const onAddressItemClone = useCallback(
        (id: string) => {
            setFieldValues((fieldValues) => {
                if (!fieldValues) {
                    return null;
                }

                const addressToClone = fieldValues.addressLines?.find((address) => address.id === id);

                if (!addressToClone) {
                    return fieldValues;
                }

                return {
                    ...fieldValues,
                    addressLines: [
                        ...(fieldValues.addressLines || []),
                        {
                            ...addressToClone,
                            id: generateUniqueAddressListItemId(),
                            defaultBilling: false,
                            defaultShipping: false,
                        },
                    ],
                };
            });

            shouldDispatchChangeEvent && changeNetSuiteRequest();
        },
        [shouldDispatchChangeEvent, changeNetSuiteRequest, setFieldValues]
    );

    const isAddressFieldInvalid = useCallback(
        (
            value: string | Reference | null,
            address: NetSuiteVendorAddressListItem,
            fieldState: domain.DocumentFieldState
        ) => showErrors && isFieldMandatory(fieldState) && !isVendorAddressEmpty(address) && !value,
        [showErrors]
    );

    const tableDef = useMemo(() => {
        const columnDefinition: ColumnDefinition<NetSuiteVendorAddressListItem>[] = [
            {
                id: 'defaultShipping',
                name: messages.defaultShipping,
                fixedWidth: 140,
                cell: (li) => (
                    <CheckboxCell>
                        <CheckboxEditor
                            value={li.defaultShipping}
                            onChange={(newValue) => {
                                onAddressItemUpdate(li.id, 'defaultShipping', newValue);
                            }}
                        />
                    </CheckboxCell>
                ),
            },
            {
                id: 'defaultBilling',
                name: messages.defaultBilling,
                fixedWidth: 120,
                cell: (li) => (
                    <CheckboxCell>
                        <CheckboxEditor
                            value={li.defaultBilling}
                            onChange={(newValue) => {
                                onAddressItemUpdate(li.id, 'defaultBilling', newValue);
                            }}
                        />
                    </CheckboxCell>
                ),
            },
            {
                id: 'label',
                name: messages.label,
                fixedWidth: 218,
                cell: (li, { invalid }) => (
                    <FocusImplementer>
                        {({ registerFocusable }) => (
                            <TextEditor
                                ref={registerFocusable}
                                invalid={invalid}
                                value={li.label || ''}
                                onChange={(newValue: string | null) => {
                                    onAddressItemUpdate(li.id, 'label', newValue || '');
                                }}
                                placeholder={messages.inputPlaceholder}
                                maxLength={netSuiteConstants.labelMaxLength}
                                theme='transparent'
                            />
                        )}
                    </FocusImplementer>
                ),
            },
        ];

        if (isFieldVisible(fieldSettings.addressLinesCountry)) {
            columnDefinition.push({
                id: 'country',
                name: messages.country,
                fixedWidth: 218,
                readonly: () => isFieldReadOnly(fieldSettings.addressLinesCountry),
                invalid: (address) =>
                    isAddressFieldInvalid(address.country, address, fieldSettings.addressLinesCountry),
                cell: (li, { invalid, readonly }) => (
                    <FocusImplementer>
                        {({ registerFocusable }) => (
                            <DropdownEditorContainer>
                                <NetSuiteCountriesDataProvider>
                                    <DropdownEditor
                                        ref={registerFocusable}
                                        invalid={invalid}
                                        disabled={readonly}
                                        value={li.country || null}
                                        singleLinePerItem={false}
                                        onChange={(value: NetSuiteVendorAddressListItem['country']) => {
                                            onAddressItemUpdate(li.id, 'country', value);
                                        }}
                                        placeholder={messages.selectPlaceholder}
                                        theme='transparent'
                                    />
                                </NetSuiteCountriesDataProvider>
                            </DropdownEditorContainer>
                        )}
                    </FocusImplementer>
                ),
            });
        }

        if (isFieldVisible(fieldSettings.addressLinesAttention)) {
            columnDefinition.push({
                id: 'attention',
                name: messages.attention,
                fixedWidth: 218,
                readonly: () => isFieldReadOnly(fieldSettings.addressLinesAttention),
                invalid: (address) =>
                    isAddressFieldInvalid(address.attention, address, fieldSettings.addressLinesAttention),
                cell: (li, { invalid, readonly }) => (
                    <FocusImplementer>
                        {({ registerFocusable }) => (
                            <TextEditor
                                ref={registerFocusable}
                                invalid={invalid}
                                disabled={readonly}
                                value={li.attention || ''}
                                onChange={(newValue: string | null) => {
                                    onAddressItemUpdate(li.id, 'attention', newValue || '');
                                }}
                                placeholder={messages.inputPlaceholder}
                                maxLength={netSuiteConstants.attentionMaxLength}
                                theme='transparent'
                            />
                        )}
                    </FocusImplementer>
                ),
            });
        }

        if (isFieldVisible(fieldSettings.addressLinesAddressee)) {
            columnDefinition.push({
                id: 'addressee',
                name: messages.addressee,
                fixedWidth: 218,
                readonly: () => isFieldReadOnly(fieldSettings.addressLinesAddressee),
                invalid: (address) =>
                    isAddressFieldInvalid(address.addressee, address, fieldSettings.addressLinesAddressee),
                cell: (li, { invalid, readonly }) => (
                    <FocusImplementer>
                        {({ registerFocusable }) => (
                            <TextEditor
                                ref={registerFocusable}
                                invalid={invalid}
                                disabled={readonly}
                                value={li.addressee || ''}
                                onChange={(newValue: string | null) => {
                                    onAddressItemUpdate(li.id, 'addressee', newValue || '');
                                }}
                                placeholder={messages.inputPlaceholder}
                                maxLength={netSuiteConstants.addresseeMaxLength}
                                theme='transparent'
                            />
                        )}
                    </FocusImplementer>
                ),
            });
        }

        if (isFieldVisible(fieldSettings.addressLinesPhone)) {
            columnDefinition.push({
                id: 'phone',
                name: messages.phone,
                fixedWidth: 218,
                readonly: () => isFieldReadOnly(fieldSettings.addressLinesPhone),
                invalid: (address) => isAddressFieldInvalid(address.phone, address, fieldSettings.addressLinesPhone),
                cell: (li, { invalid, readonly }) => (
                    <FocusImplementer>
                        {({ registerFocusable }) => (
                            <TextEditor
                                ref={registerFocusable}
                                invalid={invalid}
                                disabled={readonly}
                                value={li.phone || ''}
                                onChange={(newValue: string | null) => {
                                    onAddressItemUpdate(li.id, 'phone', newValue || '');
                                }}
                                placeholder={messages.inputPlaceholder}
                                maxLength={netSuiteConstants.addressPhoneMaxLength}
                                theme='transparent'
                            />
                        )}
                    </FocusImplementer>
                ),
            });
        }

        if (isFieldVisible(fieldSettings.addressLinesPrimaryAddress)) {
            columnDefinition.push({
                id: 'primaryAddress',
                name: messages.primaryAddress,
                fixedWidth: 218,
                readonly: () => isFieldReadOnly(fieldSettings.addressLinesPrimaryAddress),
                invalid: (address) =>
                    isAddressFieldInvalid(address.primaryAddress, address, fieldSettings.addressLinesPrimaryAddress),
                cell: (li, { invalid, readonly }) => (
                    <FocusImplementer>
                        {({ registerFocusable }) => (
                            <TextEditor
                                ref={registerFocusable}
                                invalid={invalid}
                                disabled={readonly}
                                value={li.primaryAddress || ''}
                                onChange={(newValue: string | null) => {
                                    onAddressItemUpdate(li.id, 'primaryAddress', newValue || '');
                                }}
                                placeholder={messages.inputPlaceholder}
                                maxLength={netSuiteConstants.primaryAddressMaxLength}
                                theme='transparent'
                            />
                        )}
                    </FocusImplementer>
                ),
            });
        }

        if (isFieldVisible(fieldSettings.addressLinesSecondaryAddress)) {
            columnDefinition.push({
                id: 'secondaryAddress',
                name: messages.secondaryAddress,
                fixedWidth: 218,
                readonly: () => isFieldReadOnly(fieldSettings.addressLinesSecondaryAddress),
                invalid: (address) =>
                    isAddressFieldInvalid(
                        address.secondaryAddress,
                        address,
                        fieldSettings.addressLinesSecondaryAddress
                    ),
                cell: (li, { invalid, readonly }) => (
                    <FocusImplementer>
                        {({ registerFocusable }) => (
                            <TextEditor
                                ref={registerFocusable}
                                invalid={invalid}
                                disabled={readonly}
                                value={li.secondaryAddress || ''}
                                onChange={(newValue: string | null) => {
                                    onAddressItemUpdate(li.id, 'secondaryAddress', newValue || '');
                                }}
                                placeholder={messages.inputPlaceholder}
                                maxLength={netSuiteConstants.secondaryAddressMaxLength}
                                theme='transparent'
                            />
                        )}
                    </FocusImplementer>
                ),
            });
        }

        if (isFieldVisible(fieldSettings.addressLinesState)) {
            columnDefinition.push({
                id: 'state',
                name: messages.state,
                fixedWidth: 218,
                readonly: () => isFieldReadOnly(fieldSettings.addressLinesState),
                invalid: (address) => isAddressFieldInvalid(address.state, address, fieldSettings.addressLinesState),
                cell: (li, { invalid, readonly }) => (
                    <FocusImplementer>
                        {({ registerFocusable }) => (
                            <TextEditor
                                ref={registerFocusable}
                                invalid={invalid}
                                disabled={readonly}
                                value={li.state || ''}
                                onChange={(newValue: string | null) => {
                                    onAddressItemUpdate(li.id, 'state', newValue || '');
                                }}
                                placeholder={messages.inputPlaceholder}
                                maxLength={netSuiteConstants.stateMaxLength}
                                theme='transparent'
                            />
                        )}
                    </FocusImplementer>
                ),
            });
        }

        if (isFieldVisible(fieldSettings.addressLinesCity)) {
            columnDefinition.push({
                id: 'city',
                name: messages.city,
                fixedWidth: 218,
                readonly: () => isFieldReadOnly(fieldSettings.addressLinesCity),
                invalid: (address) => isAddressFieldInvalid(address.city, address, fieldSettings.addressLinesCity),
                cell: (li, { invalid, readonly }) => (
                    <FocusImplementer>
                        {({ registerFocusable }) => (
                            <TextEditor
                                ref={registerFocusable}
                                invalid={invalid}
                                disabled={readonly}
                                value={li.city || ''}
                                onChange={(newValue: string | null) => {
                                    onAddressItemUpdate(li.id, 'city', newValue || '');
                                }}
                                placeholder={messages.inputPlaceholder}
                                maxLength={netSuiteConstants.cityMaxLength}
                                theme='transparent'
                            />
                        )}
                    </FocusImplementer>
                ),
            });
        }

        if (isFieldVisible(fieldSettings.addressLinesZip)) {
            columnDefinition.push({
                id: 'zip',
                name: messages.zip,
                fixedWidth: 120,
                readonly: () => isFieldReadOnly(fieldSettings.addressLinesZip),
                invalid: (address) => isAddressFieldInvalid(address.zip, address, fieldSettings.addressLinesZip),
                cell: (li, { invalid, readonly }) => (
                    <FocusImplementer>
                        {({ registerFocusable }) => (
                            <TextEditor
                                ref={registerFocusable}
                                invalid={invalid}
                                disabled={readonly}
                                value={li.zip || ''}
                                onChange={(newValue: string | null) => {
                                    onAddressItemUpdate(li.id, 'zip', newValue || '');
                                }}
                                placeholder={messages.inputPlaceholder}
                                maxLength={netSuiteConstants.zipMaxLength}
                                theme='transparent'
                            />
                        )}
                    </FocusImplementer>
                ),
            });
        }

        return columnDefinition;
    }, [fieldSettings, onAddressItemUpdate, isAddressFieldInvalid]);

    return {
        tableDef,
        onAddressListItemAdd,
        onAddressListItemRemove,
        onAddressItemClone,
    };
};
